import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowDownTrayIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { Link } from 'react-router-dom';
import useDesktopPwaStatus from '../hooks/useDesktopPwaStatus';
import {
  PWA_INSTALL_DISMISS_KEY,
  applyPwaUpdate,
  canShowInstallPrompt,
  isDesktopPwaTarget,
  promptPwaInstall,
  shouldOfferPwaInstall,
} from '../utils/pwa';

const PUBLIC_PATHS = new Set([
  '/',
  '/planes',
  '/funcionalidades',
  '/quienes-somos',
  '/ayuda',
  '/contacto',
  '/terminos',
  '/privacidad',
  '/login',
  '/register',
  '/forgot-password',
  '/restablecer-clave',
  '/pago/resultado',
  '/activar-cuenta',
]);

function isAppRoute(pathname) {
  if (PUBLIC_PATHS.has(pathname)) return false;
  if (pathname.startsWith('/contador/compartido')) return false;
  if (pathname.startsWith('/admin')) return false;
  if (pathname === '/completar-perfil' || pathname === '/aceptar-contador') return false;
  return true;
}

const PwaInstallBanner = () => {
  const { user } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const { pathname } = useLocation();

  const [desktop, setDesktop] = useState(() => isDesktopPwaTarget());
  const [installable, setInstallable] = useState(() => canShowInstallPrompt());
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(PWA_INSTALL_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const showOnRoute = isAppRoute(pathname);
  const { kind } = useDesktopPwaStatus();

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const sync = () => setDesktop(isDesktopPwaTarget());
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const onInstallable = () => setInstallable(canShowInstallPrompt());
    const onUpdate = () => setUpdateAvailable(true);
    window.addEventListener('contsuite:pwa-installable', onInstallable);
    window.addEventListener('contsuite:pwa-update', onUpdate);
    return () => {
      window.removeEventListener('contsuite:pwa-installable', onInstallable);
      window.removeEventListener('contsuite:pwa-update', onUpdate);
    };
  }, []);

  if (!user || !showOnRoute || !desktop) return null;
  if (!shouldOfferPwaInstall()) return null;
  if (kind !== 'not-installed' && !updateAvailable) return null;

  if (updateAvailable) {
    const box = modoOscuro
      ? 'bg-gray-900 border-gray-700 text-white'
      : 'bg-white border-gray-200 text-gray-900';
    return (
      <div className="fixed bottom-6 right-6 z-[60] flex justify-end pointer-events-none max-w-md">
        <div className={`pointer-events-auto w-full rounded-xl border shadow-lg px-4 py-3 flex items-start gap-3 ${box}`}>
          <ArrowPathIcon className="w-5 h-5 text-[#1F80FF] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Nueva versión disponible</p>
            <p className={`text-xs mt-0.5 ${modoOscuro ? 'text-gray-400' : 'text-gray-500'}`}>
              Actualizá la app de escritorio para tener los últimos cambios.
            </p>
            <button
              type="button"
              onClick={() => applyPwaUpdate()}
              className="mt-2 text-xs font-semibold text-[#1F80FF] hover:underline"
            >
              Actualizar ahora
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!installable || dismissed) return null;

  const box = modoOscuro
    ? 'bg-gray-900 border-gray-700 text-white'
    : 'bg-white border-gray-200 text-gray-900';
  const sub = modoOscuro ? 'text-gray-400' : 'text-gray-500';

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(PWA_INSTALL_DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const onInstall = async () => {
    const result = await promptPwaInstall();
    if (result.outcome === 'accepted') setInstallable(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-[55] flex justify-end pointer-events-none max-w-md">
      <div className={`pointer-events-auto w-full rounded-xl border shadow-lg px-4 py-3 ${box}`}>
        <div className="flex items-start gap-3">
          <ArrowDownTrayIcon className="w-5 h-5 text-[#1F80FF] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Instalá Contsuite en tu PC</p>
            <p className={`text-xs mt-0.5 ${sub}`}>
              Abrila como app de escritorio: en Chrome o Edge usá el ícono de instalar en la barra de direcciones
              o el menú ⋮ → Instalar Contsuite.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onInstall}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#1F80FF] text-white"
              >
                Instalar en esta PC
              </button>
              <button
                type="button"
                onClick={dismiss}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${
                  modoOscuro ? 'border-gray-600 text-gray-300' : 'border-gray-200 text-gray-600'
                }`}
              >
                Ahora no
              </button>
              <Link
                to="/cuenta?tab=profile#app-escritorio"
                onClick={dismiss}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg underline underline-offset-2 ${
                  modoOscuro ? 'text-gray-300' : 'text-gray-600'
                }`}
              >
                Instalar desde Cuenta
              </Link>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className={`p-1 rounded-md shrink-0 ${modoOscuro ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
            aria-label="Cerrar"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PwaInstallBanner;
